import { Flex, Form } from 'antd';
import { FormInstance } from 'antd/es/form/hooks/useForm';
import Title from 'antd/es/typography/Title';
import { useTranslation } from 'translations/hooks';

import { useAppContext } from 'business/contextProviders/useAppContext';
import { DecimalInput } from 'ui/form/decimal-input';

import { UpdateSteeringFormValues } from './types';

interface UpdateRingFormProps {
  form: FormInstance<UpdateSteeringFormValues>;
  save: (row: UpdateSteeringFormValues) => void;
}

export const UpdateRingForm: React.FC<UpdateRingFormProps> = ({
  form,
  save,
}) => {
  const { t } = useTranslation();

  const { currentConstructionSite } = useAppContext();
  const isArticulated = currentConstructionSite?.isArticulated;

  return (
    <Form<UpdateSteeringFormValues>
      form={form}
      layout="vertical"
      onFinish={save}
    >
      <Title level={5}>{t('steering.table.columns.horizontalPosition')}</Title>

      <Flex gap={8}>
        <Form.Item<UpdateSteeringFormValues>
          style={{ flex: 1 }}
          name="rearHorizontalPosition"
          label={t('steering.table.columns.rear')}
        >
          <DecimalInput style={{ width: '100%' }} decimalLimit={0} />
        </Form.Item>

        <Form.Item<UpdateSteeringFormValues>
          style={{ flex: 1 }}
          name="frontHorizontalPosition"
          label={t('steering.table.columns.front')}
        >
          <DecimalInput style={{ width: '100%' }} decimalLimit={0} />
        </Form.Item>
        {isArticulated ? (
          <Form.Item<UpdateSteeringFormValues>
            style={{ flex: 1 }}
            name="cuttingWheelHorizontalPosition"
            label={t('steering.table.columns.cuttingWheel')}
          >
            <DecimalInput style={{ width: '100%' }} decimalLimit={0} />
          </Form.Item>
        ) : null}
      </Flex>

      <Flex gap={8}>
        <Form.Item<UpdateSteeringFormValues>
          style={{ flex: 1 }}
          name="rearHorizontalTrend"
          label={t('steering.table.columns.trend', {
            context: isArticulated ? 'rear' : undefined,
          })}
        >
          <DecimalInput
            style={{ width: '100%' }}
            decimalLimit={2}
            step={0.01}
          />
        </Form.Item>
        {isArticulated ? (
          <Form.Item<UpdateSteeringFormValues>
            style={{ flex: 1 }}
            name="frontHorizontalTrend"
            label={t('steering.table.columns.trend', {
              context: 'front',
            })}
          >
            <DecimalInput
              style={{ width: '100%' }}
              decimalLimit={2}
              step={0.01}
            />
          </Form.Item>
        ) : null}
      </Flex>

      <Title level={5}>{t('steering.table.columns.verticalPosition')}</Title>
      <Flex gap={8}>
        <Form.Item<UpdateSteeringFormValues>
          style={{ flex: 1 }}
          name="rearVerticalPosition"
          label={t('steering.table.columns.rear')}
        >
          <DecimalInput style={{ width: '100%' }} decimalLimit={0} />
        </Form.Item>
        <Form.Item<UpdateSteeringFormValues>
          style={{ flex: 1 }}
          name="frontVerticalPosition"
          label={t('steering.table.columns.front')}
        >
          <DecimalInput style={{ width: '100%' }} decimalLimit={0} />
        </Form.Item>
        {isArticulated ? (
          <Form.Item<UpdateSteeringFormValues>
            style={{ flex: 1 }}
            name="cuttingWheelVerticalPosition"
            label={t('steering.table.columns.cuttingWheel')}
          >
            <DecimalInput style={{ width: '100%' }} decimalLimit={0} />
          </Form.Item>
        ) : null}
      </Flex>
      <Flex gap={8}>
        <Form.Item<UpdateSteeringFormValues>
          style={{ flex: 1 }}
          name="rearVerticalTrend"
          label={t('steering.table.columns.trend', {
            context: isArticulated ? 'rear' : undefined,
          })}
        >
          <DecimalInput
            style={{ width: '100%' }}
            decimalLimit={2}
            step={0.01}
          />
        </Form.Item>
        {isArticulated ? (
          <Form.Item<UpdateSteeringFormValues>
            style={{ flex: 1 }}
            name="frontVerticalTrend"
            label={t('steering.table.columns.trend', {
              context: 'front',
            })}
          >
            <DecimalInput
              style={{ width: '100%' }}
              decimalLimit={2}
              step={0.01}
            />
          </Form.Item>
        ) : null}
      </Flex>

      <Title level={5}>{t('steering.table.columns.skirtClearance')}</Title>
      <Flex gap={8}>
        <Form.Item<UpdateSteeringFormValues>
          style={{ flex: 1 }}
          name="leftSkirtClearance"
          label={t('steering.table.columns.left')}
        >
          <DecimalInput style={{ width: '100%' }} decimalLimit={0} />
        </Form.Item>
        <Form.Item<UpdateSteeringFormValues>
          style={{ flex: 1 }}
          name="rightSkirtClearance"
          label={t('steering.table.columns.right')}
        >
          <DecimalInput style={{ width: '100%' }} decimalLimit={0} />
        </Form.Item>
        <Form.Item<UpdateSteeringFormValues>
          style={{ flex: 1 }}
          name="upperSkirtClearance"
          label={t('steering.table.columns.upper')}
        >
          <DecimalInput style={{ width: '100%' }} decimalLimit={0} />
        </Form.Item>
        <Form.Item<UpdateSteeringFormValues>
          style={{ flex: 1 }}
          name="lowerSkirtClearance"
          label={t('steering.table.columns.lower')}
        >
          <DecimalInput style={{ width: '100%' }} decimalLimit={0} />
        </Form.Item>
      </Flex>
    </Form>
  );
};
